import Papa from 'papaparse';
import { type PropsWithChildren, useState } from 'react';
import { DropZone, type DropZoneProps } from '.';
import { toastError } from '../utils/Utils';
import { ErrorField } from './ErrorField';

interface Props<T extends object = object>
  extends Omit<DropZoneProps, 'allowedItemTypes' | 'allowedItemKind' | 'onFilesDrop'> {
  transformHeader?: (header: string, index: number) => string;
  transform?: (value: string, header: string) => T[keyof T] | undefined;
  onCSVDrop: (items: Array<T>, errors: Array<Papa.ParseError>) => void;
  onParsingError?: (errors: Array<Papa.ParseError>) => void;
  beforeFirstChunk?: (chunk: string) => string;
  skipFirstNLines?: number;
  quoteChar?: '"' | "'";
}

export const CSVDropZone = <T extends object>({
  children,
  transformHeader,
  transform,
  onCSVDrop,
  onParsingError,
  beforeFirstChunk,
  quoteChar,
  ...props
}: PropsWithChildren<Props<T>>) => {
  const [errors, setErrors] = useState<Array<Papa.ParseError>>([]);

  const handleFilesDrop = async (files: FileList) => {
    setErrors([]);

    try {
      for (const file of files) {
        const res = await parseCSVFile<T>({
          file,
          transformHeader,
          transform,
          beforeFirstChunk,
          quoteChar,
        });
        onCSVDrop(res.data, res.errors);
      }
    } catch (err) {
      if (Array.isArray(err) && err[0] != null) {
        setErrors(err);
        onParsingError?.(err);
      }
      if (err instanceof Error) {
        toastError(`Failed to parse: ${err.message}`);
        console.error(err);
      }
    }
  };
  return (
    <>
      <DropZone
        onFilesDrop={handleFilesDrop}
        allowedItemKind="file"
        allowedItemTypes={['.csv', 'text/csv', 'text/plain']}
        {...props}
      >
        {children}
      </DropZone>
      <ErrorField errors={errors} />
    </>
  );
};

function parseCSVFile<T extends object>({
  file,
  transform = (v) => v as T[keyof T],
  transformHeader = (h) => h,
  beforeFirstChunk,
  quoteChar = '"',
}: {
  file: File;
  transformHeader?: (header: string, index: number) => string;
  transform?: (value: string, header: string) => T[keyof T] | undefined;
  beforeFirstChunk?: (chunk: string) => string;
  quoteChar?: string;
}): Promise<Papa.ParseResult<T>> {
  return new Promise((resolve, reject) => {
    Papa.parse<T>(file, {
      skipEmptyLines: true,
      beforeFirstChunk,
      header: true,
      quoteChar,
      comments: '#',
      transform,
      transformHeader,
      error(errors) {
        reject(errors);
      },
      complete(results) {
        if (results.errors.length > 0 && results.errors.some((e) => e.code !== 'TooManyFields')) {
          reject(results.errors);
          return;
        }

        if (results.data.length < 1) {
          reject(new Error('Too few rows'));
          return;
        }

        if (Object.keys(results.data[0]).length < 1) {
          reject(new Error('Too few columns'));
          return;
        }

        resolve(results);
      },
    });
  });
}
